// 导航相关函数

import { bridge, BRIDGE_METHODS } from '@h5-journey/bridge'
import { UserInfo, useUserStore } from '@h5-journey/stores'
import wx from 'weixin-js-sdk'
import { getSdkFn } from './common'
import { isInApp, isInWeChatMiniProgram } from './env'
import { querySerialize } from './object'

const isInWeChat = isInWeChatMiniProgram()

// 获取所有的路径参数
export const getAllQueryString = (url: string) => {
  // str为？之后的参数部分字符串
  const str = url.substr(url.indexOf('?') + 1)
  // arr每个元素都是完整的参数键值
  const arr = str.split('&')
  // result为存储参数键值的集合
  const result: Record<string, string> = {}
  for (let i = 0; i < arr.length; i++) {
    // item的两个元素分别为参数名和参数值
    const item = arr[i].split('=')
    result[item[0]] = item[1]
  }
  return result
}

// 移除url的登录,环境等公共参数，因为登陆后原生会重新拼接上去信息,isOnleyRemoveLoginInfo是否只移除登录信息,保留env等环境参数,否则移除所有公共参数
export const removePubParamsOfUrl = (url = '', isOnleyRemoveLoginInfo?: boolean) => {
  const splitArr = url.split('?')
  if (splitArr.length < 2) {
    return url
  }
  let restParams = {}
  if (isOnleyRemoveLoginInfo) {
    // eslint-disable-next-line
    const { loginId, loginToken, memberId, mobile, ...rParams } = getAllQueryString(url) || {}
    restParams = rParams
  } else {
    /* eslint-disable */
    const {
      city,
      cityId,
      isAbroad,
      environment,
      env,
      loginId,
      loginToken,
      memberId,
      mobile,
      sourceflag,
      sourceFlag,
      code,
      terminal,
      ...rParams
    } = getAllQueryString(url) || {}
    /* eslint-enable */
    restParams = rParams
  }
  return `${splitArr[0]}?${querySerialize(restParams)}`
}

// 返回首页
export function goHome() {
  if (isInWeChatMiniProgram()) {
    wx?.miniProgram?.switchTab?.({
      url: `/pages/index/index`
    })
    return
  }
  bridge.invoke(BRIDGE_METHODS.GO_HOME)
}

// 防重入标记：并发 401 或多处调用 toLogin 时，只执行第一次跳转
let isRedirectingToLogin = false

// 跳转登录
export const toLogin = async (
  onLoginSuccess?: () => void,
  hasHistory = true,
  options?: { loginPath?: string }
) => {
  if (isRedirectingToLogin) return
  isRedirectingToLogin = true

  const loginPath = options?.loginPath || '/login'
  try {
    if (isInWeChat) {
      // 微信小程序环境去原生登录页登录
      getSdkFn()?.miniProgram?.[hasHistory ? 'navigateTo' : 'redirectTo']({
        url: `/pages/login/index?url=${encodeURIComponent(removePubParamsOfUrl(location.href))}`
      })
      return
    }

    // APP环境调用原生登录
    const inApp = isInApp()
    if (inApp) {
      const res = await bridge.invokeSync<UserInfo>(BRIDGE_METHODS.LOGIN)
      if (res && res.result && res.result.code === 0 && res.result.data) {
        // 登录成功后重新获取用户信息
        const userStore = useUserStore()
        const result = res.result
        if (result.code !== 0) {
          console.error('登陆成功111111', result.errorMsg)
        } else {
          console.log('登陆成功111111成功:', result.data)
          userStore.setUserInfo(result.data)
        }

        if (onLoginSuccess) {
          onLoginSuccess()
        } else {
          // 刷新当前页面以更新状态
          window.location.reload()
        }
      }
      return
    }

    // Web 浏览器环境：跳转到登录页，携带当前页面 URL
    const currentUrl = encodeURIComponent(removePubParamsOfUrl(location.href))
    const base = (import.meta.env.VITE_BASE_URL || '/').replace(/\/$/, '')
    window.location.replace(`${base}${loginPath}?url=${currentUrl}`)
  } catch (error) {
    console.error('登录失败:', error)
  } finally {
    // 页跳转失败时释放标记，允许重试
    isRedirectingToLogin = false
  }
}

type ProjectName = 'client' | 'newJourney'

export const routePrefix: Record<ProjectName, string> = {
  client: '/#/commonModule/pages',
  newJourney: '/new-journey'
}

interface AppOptions {
  showClose?: boolean
}

/**
 * 构建项目完整 URL
 * @param url - 相对路径或完整 URL
 * @param projectName - 项目名称，用于拼接路由前缀
 * @returns 完整的目标 URL
 */
export function buildProjectUrl(url: string, projectName?: ProjectName): string {
  // 如果已经是完整 URL，直接返回
  if (/^https?:\/\//.test(url)) {
    return url
  }

  const prefix = projectName ? routePrefix[projectName] : ''
  return `${window.location.origin}${prefix}${url}`
}

interface NavigateToWebviewOptions {
  url?: string
  projectName?: ProjectName
  type?: 'push' | 'go' | 'replace'
  title?: string
  fullUrl?: string
  cb?: () => void
  hasHistory?: boolean
  appOptions?: AppOptions
}

// 先跳转登录然后再跳转
export const gotoLoginAndRedirect = (url: string, params: string = '') => {
  if (!isInWeChatMiniProgram()) {
    return
  }
  wx?.miniProgram?.navigateTo({
    url: `/pages/login/index?url=${encodeURIComponent(url)}${params}`
  })
}

/**
 * 跳转flutter webview页面，支持push和go
 * @param {string} url 跳转的url，无需携带域名，如需参数，需要拼接在路由之后
 * @param {string} projectName 项目名称，client或newJourney
 * @param {string} type 跳转类型，push或go
 * @param {string} title 页面标题 必传，不传会丢失导航头
 * @param {string} [fullUrl] 完整的url，如果传递此参数，将忽略url和projectName参数，直接使用此完整url
 * @param {string} [cb] then callback
 * @param {Object} [appOptions] App环境专属配置，仅App生效
 * @param {boolean} [appOptions.showClose=false] 是否展示关闭按钮
 * @returns {void} 无返回值
 */
export const NavigateToWebview = ({
  url,
  projectName = 'client',
  type = 'push',
  title,
  fullUrl,
  cb,
  hasHistory = true,
  appOptions
}: NavigateToWebviewOptions) => {
  const targetUrl = fullUrl || (url ? buildProjectUrl(url, projectName) : '')

  if (isInWeChatMiniProgram()) {
    const miniProgramUrl = `/pages/webview/index?url=${encodeURIComponent(targetUrl)}`
    try {
      const action = hasHistory ? 'navigateTo' : 'redirectTo'
      wx?.miniProgram?.[action]?.({ url: miniProgramUrl })
    } catch (error) {
      console.error('小程序跳转失败:', error)
    }
    return
  }

  // H5 浏览器环境跳转
  if (!isInWeChatMiniProgram() && !window.flutter_inappwebview) {
    console.log(targetUrl, '链接')
    window.location.href = targetUrl
    return
  }

  let name = `/web?url=${encodeURIComponent(targetUrl)}`
  if (title) {
    name += `&title=${title}`
  }

  // App 原生侧通过 URL query 读取 showClose 参数控制导航栏关闭按钮展示
  if (appOptions) {
    const { showClose } = appOptions
    if (showClose) {
      name += `&showClose=1`
    }
  }

  console.log(name, '------🚀🚀✈️')

  if (hasHistory) {
    console.log(name, '---------name')
    bridge
      .invokeSync(BRIDGE_METHODS.OPEN_PAGE, { type, name })
      .then(() => {
        cb && cb()
      })
      .catch((error) => {
        console.error('跳转失败:', error)
      })
  } else {
    bridge
      .invokeSync(BRIDGE_METHODS.ROUTER_POP)
      .then(() => bridge.invokeSync(BRIDGE_METHODS.OPEN_PAGE, { type, name }))
      .then(() => {
        cb && cb()
      })
      .catch((error) => {
        console.error('跳转失败:', error)
      })
  }
}

/**
 * 返回上一页
 * - App 环境通过原生 bridge 返回
 * - 微信小程序环境通过 wx.miniProgram.navigateBack 返回（history.back 在小程序首页 webview 内无效）
 * - 其他环境走浏览器 history.back
 */
export function goBack() {
  if (isInApp()) {
    bridge.invoke(BRIDGE_METHODS.GO_BACK)
    return
  }
  if (isInWeChatMiniProgram()) {
    wx?.miniProgram?.navigateBack?.({ delta: 1 } as any)
    return
  }
  history.back()
}
